'use client'

import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import { useSession } from 'next-auth/react'
import { X, AlertCircle, Heart } from 'lucide-react'
import { ALERT_BANNER_DISMISS_KEY } from './alertBannerKey'
import type { PromoBarCopy } from './promoBarCopy'

/** Persist a dismissal where the SERVER can read it (components/AlertBanner.tsx), not only in localStorage. */
function writeDismissCookie() {
  try { document.cookie = `${ALERT_BANNER_DISMISS_KEY}=1; Max-Age=31536000; Path=/; SameSite=Lax` } catch {}
}

export default function AlertBannerClient({ initialDismissed = false, copy }: { initialDismissed?: boolean; copy: PromoBarCopy }) {
  const [isVisible, setIsVisible] = useState(!initialDismissed)
  const pathname = usePathname()
  const { data: session } = useSession()
  // Post-go-live copy version (bump the key so the old dismissed state doesn't hide the new notice).
  const dismissKey = ALERT_BANNER_DISMISS_KEY
  // WHAT the bar says is decided on the server (components/AlertBanner.tsx via promoBarCopy.ts), so the server
  // HTML and the hydrated bar always carry the same words.

  useEffect(() => {
    try {
      // localStorage persists across tabs and survives sessionStorage.clear() during logout
      const isDismissed = localStorage.getItem(dismissKey)
      // A dismissal from before the cookie existed: hide, and write the cookie so the server knows next
      // time. That costs one shift on this visit only (register promo-bar-offset-set-after-hydration-1031).
      if (isDismissed) { setIsVisible(false); if (!initialDismissed) writeDismissCookie() }
    } catch {}
  }, [])

  const handleDismiss = () => {
    setIsVisible(false)
    try { localStorage.setItem(dismissKey, 'true') } catch {}
    writeDismissCookie()
  }

  // [OK] Hide banner on chart pages (frequently opened in new tabs)
  if (pathname?.startsWith('/chart/')) return null
  // [OK] Hide banner on the live alerts dashboard (it competes with the fixed navbar/sticky command bar)
  if (pathname === '/alerts') return null
  // Admins and subscribers are dropped on the SERVER (no bar, no reserve). This is the backstop for a session
  // that changed after the page was rendered (just subscribed), costing at most one shift on that page.
  if (session?.user?.isAdmin || session?.user?.isSubscriber) return null

  if (!isVisible) return null

  return (
    // ONE THIN ROW, calm glass (matches the redesigned SiteBanner). This used to be a full paragraph in
    // a bright amber gradient — on mobile it wrapped to ~10 lines and, because ConditionalMain pads the
    // page by the banner's height, buried the entire hero + primary CTA below the fold. Now: title always,
    // the detail sentence + the donate CTA only from sm up, so the mobile first screen is the product,
    // not a subscription wall.
    // h-[47px] is LOAD-BEARING: app/globals.css reserves exactly this much at server paint (--alert-banner-h) so the
    // page does not shift when the bar's offset is applied. A FIXED height (with the one truncated row below) means
    // longer copy or another font cannot silently change it; promoBarServerPaint.test.ts fails if the two drift
    // (register promo-bar-offset-set-after-hydration-1031).
    <div data-alert-banner="" className="fixed top-0 left-0 right-0 z-[9999] h-[47px] border-b border-amber-400/25 bg-white/95 dark:bg-zinc-950/95 backdrop-blur-md">
      <span aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-amber-400/70 via-amber-300/40 to-transparent" />
      <div className="container mx-auto flex h-full items-center gap-2.5 px-3 py-2 sm:gap-3 sm:px-6">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-400/10 text-amber-700 dark:text-amber-300 ring-1 ring-amber-400/20">
          <AlertCircle className="h-4 w-4" />
        </span>
        <p className="min-w-0 flex-1 truncate text-[13px] leading-tight text-zinc-700 dark:text-zinc-300">
          <span className="font-semibold text-zinc-900 dark:text-white">{copy.lead}</span>
          <span className="hidden text-zinc-600 dark:text-zinc-400 sm:inline">{copy.detail}</span>
        </p>
        <a
          href={copy.href}
          className="inline-flex shrink-0 items-center rounded-lg bg-amber-500 px-3.5 py-1.5 text-[12px] font-bold text-black transition-colors hover:bg-amber-400"
        >
          Plans<span className="hidden sm:inline">&nbsp;&amp; pricing</span>
        </a>
        <a
          href="/donate"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden shrink-0 items-center gap-1.5 rounded-lg bg-emerald-400/[0.06] px-3 py-1.5 text-[12px] font-semibold text-emerald-700 dark:text-emerald-300 ring-1 ring-inset ring-emerald-400/25 transition-colors hover:bg-emerald-400/[0.12] hover:text-emerald-700 dark:hover:text-emerald-200 sm:inline-flex"
        >
          <Heart className="h-3.5 w-3.5 fill-current" />
          Donate
        </a>
        <button
          onClick={handleDismiss}
          className="shrink-0 rounded-md p-1.5 text-zinc-500 transition-colors hover:text-zinc-700 dark:hover:text-zinc-300"
          aria-label="Dismiss notice"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

